<template>
  <div>
    <!-- Controls Row: Search and View Toggle -->
    <div class="d-flex justify-content-between align-items-center mt-3 mb-3">
      <!-- Search Group (takes up remaining space) -->
      <div class="input-group">
        <!-- Search Input -->
        <input
          v-model="filters.search"
          @keyup.enter="handleSearchEnter"
          type="text"
          @focus="isSearchFocused = true"
          @blur="isSearchFocused = false"
          ref="searchInput"
          class="form-control filter-input"
          maxlength="256"
          :placeholder="searchPlaceholder"
        />

        <!-- Clear Search Button -->
        <button
          v-if="filters.search"
          @click="clearSearch"
          class="btn btn-outline-secondary close"
          type="button"
        >
          <i class="bi bi-x"></i>
        </button>

        <!-- View Grid Toggle Buttons -->
        <button
          v-if="
            (filters.search || !useSemanticSearch || mode != 'all') &&
            !isSearchFocused
          "
          type="button"
          class="btn"
          :class="
            displayMode === 'grid' ? 'btn-secondary' : 'btn-outline-secondary'
          "
          @click="setDisplayMode('grid')"
          title="Сетка"
        >
          <i class="bi bi-grid-3x3-gap-fill"></i>
        </button>

        <!-- View List Toggle Buttons -->
        <button
          v-if="
            (filters.search || !useSemanticSearch || mode != 'all') &&
            !isSearchFocused
          "
          type="button"
          class="btn"
          :class="
            displayMode === 'list' ? 'btn-secondary' : 'btn-outline-secondary'
          "
          @click="setDisplayMode('list')"
          title="Список"
        >
          <i class="bi bi-list"></i>
        </button>

        <!-- Search History Dropdown -->
        <button
          v-if="!isSearchFocused"
          class="btn btn-outline-secondary dropdown-toggle history"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="true"
        >
          <i class="bi bi-clock-history"></i>
        </button>

        <!-- Dropdown Menu -->
        <ul class="dropdown-menu search-history-dropdown">
          <li class="p-2">
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Фильтр истории..."
              v-model="historyFilterText"
              @click.stop
            />
          </li>
          <li
            v-if="filteredSearchHistory.length === 0 && historyFilterText"
            class="dropdown-item text-muted disabled"
          >
            Нет совпадений
          </li>
          <li v-for="term in filteredSearchHistory" :key="term">
            <div
              class="dropdown-item d-flex justify-content-between align-items-center"
            >
              <span
                @click="selectSearchTerm(term)"
                class="search-history-term"
                >{{ term }}</span
              >
              <button
                type="button"
                class="btn btn-sm btn-link text-secondary"
                @click.stop="removeSearchTerm(term)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </li>
        </ul>

        <!-- Search Button (visible on focus) -->
        <button
          v-if="isSearchFocused"
          @mousedown.prevent="handleSearchEnter"
          class="btn btn-secondary-outline"
          type="button"
          title="Поиск"
        >
          <i class="bi bi-search"></i>
        </button>
      </div>
    </div>

    <!-- Search mode toggle -->
    <div
      v-if="mode === 'all' && showSearchModeTogle"
      class="form-check form-switch mt-3 mb-3"
    >
      <input
        class="form-check-input"
        type="checkbox"
        role="switch"
        id="semanticSearchToggle"
        v-model="useSemanticSearch"
        @change="saveSearchMode"
      />
      <label class="form-check-label" for="semanticSearchToggle"
        >Использовать семантический поиск (AI)</label
      >
    </div>

    <!-- Search mode toggle -->
    <div
      v-if="mode === 'all' && showSearchModeTogle && useSemanticSearch"
      class="form-check form-switch mt-3 mb-3"
    >
      <input
        class="form-check-input"
        type="checkbox"
        role="switch"
        id="semanticSearchGenerateToggle"
        v-model="useSearchGenerate"
        @change="saveSearchMode"
      />
      <label class="form-check-label" for="semanticSearchToggle"
        >Использовать преобразование запроса (AI)</label
      >
    </div>

    <!-- Embedding Engine Toggle (Admin only) -->
    <div
      v-if="mode === 'all' && showSearchModeTogle && useSemanticSearch"
      class="mt-3 mb-3"
    >
      <div class="btn-group" role="group" aria-label="Embedding Engine">
        <input
          type="radio"
          class="btn-check"
          name="embeddingEngine"
          id="engineOpenAI"
          autocomplete="off"
          value="openai"
          v-model="embeddingEngine"
          @change="saveFiltersAndFetch"
        />
        <label class="btn btn-outline-primary" for="engineOpenAI">OpenAI</label>

        <input
          type="radio"
          class="btn-check"
          name="embeddingEngine"
          id="engineWord2Vec"
          autocomplete="off"
          value="word2vec"
          v-model="embeddingEngine"
          @change="saveFiltersAndFetch"
        />
        <label class="btn btn-outline-primary" for="engineWord2Vec"
          >Word2Vec</label
        >
      </div>
    </div>

    <!-- Filters -->
    <div
      v-if="mode === 'all' && showFilters"
      class="input-group inner-addon mt-3 mb-3 filter"
    >
      <!-- Dish type  -->
      <select
        v-model="filters.dishtype"
        class="form-select dishtype"
        @change="saveFiltersAndFetch"
      >
        <option value="">Тип блюда</option>
        <option
          v-for="dishtype in dishTypes"
          :key="dishtype.id"
          :value="dishtype.name"
        >
          {{ dishtype.name }}
        </option>
      </select>

      <!-- Category -->
      <select
        v-model="filters.category"
        class="form-select category"
        @change="saveFiltersAndFetch"
      >
        <option value="">Категория</option>
        <option
          v-for="category in categories"
          :key="category.id"
          :value="category.name"
        >
          {{ category.name }}
        </option>
      </select>

      <!-- Max time -->
      <input
        v-model="filters.maxTime"
        class="form-control maxTime"
        type="number"
        @change="saveFiltersAndFetch"
        placeholder="Макс. время, мин"
      />

      <!-- Search button -->
      <!-- <button @click="fetchRecipes" class="btn btn-primary">
        <i class="bi bi-search"></i>
      </button> -->
    </div>

    <!-- Loading Spinner -->
    <div v-if="isLoading && recipes.length === 0" class="text-center mt-4 mb-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
      <p class="mt-2">Идет поиск рецептов...</p>
    </div>

    <!-- Recipes list -->
    <div :class="['recipe-list-container', displayModeClass]" class="mb-3">
      <div
        v-for="(recipe, index) in recipes"
        :key="recipe.id"
        class="recipe-item-wrapper"
      >
        <!-- Divider -->
        <hr v-if="displayMode !== 'grid' && index !== 0" class="mt-4 mb-4" />

        <!-- List View -->
        <RecipeView
          v-if="displayMode === 'list'"
          :recipe="recipe"
          :short="true"
          @show-full="showFullRecipe"
        />
        <!-- Grid View -->
        <RecipeCardPopular
          v-if="displayMode === 'grid'"
          class="mt-2"
          :recipe="recipe"
          :mode="mode"
          @show-full-recipe="showFullRecipe"
          @status-changed="handleStatusChange"
        />
      </div>
    </div>

    <!-- Similar Recipes (from Semantic Search) -->
    <div v-if="similarRecipes.length > 0" class="mt-4">
      <h4 v-if="recipes.length > 0">Похожие рецепты:</h4>
      <div :class="['recipe-list-container', displayModeClass]" class="mb-3">
        <div
          v-for="recipe in similarRecipes"
          :key="`similar-${recipe.id}`"
          class="recipe-item-wrapper"
        >
          <RecipeCardPopular
            v-if="displayMode === 'grid'"
            class="mt-2"
            :recipe="recipe"
            :mode="mode"
            @show-full-recipe="showFullRecipe"
            @status-changed="handleStatusChange"
          />
          <RecipeView
            v-if="displayMode === 'list'"
            :recipe="recipe"
            :short="true"
            @show-full="showFullRecipe"
          />
        </div>
      </div>
    </div>

    <!-- Loading More Spinner -->
    <div v-if="isLoading && recipes.length > 0" class="text-center mt-3 mb-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <!-- Semantic Loading Spinner -->
    <div v-if="isSemanticLoading" class="text-center mt-3 mb-4">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Семантический поиск...</span>
      </div>
      <p class="mt-2 text-secondary small">Ищем похожие рецепты...</p>
    </div>

    <!-- AI Answer Block -->
    <div v-if="aiAnswer" class="card mt-3 mb-4">
      <div class="card-body">
        <p class="card-text" v-html="formattedAiAnswer"></p>
        <div class="text-end">
          <button @click="continueDialog" class="btn btn-sm btn-primary">
            Продолжить диалог
          </button>
        </div>
      </div>
    </div>

    <!-- Chat Component Placeholder -->
    <div v-if="showChat" :key="chatComponentKey" class="search-chat">
      <ChatRecipe chat-type="search" :id="activeChatId" />
    </div>

    <!-- "More" button for semantic search -->
    <div
      v-if="
        useSemanticSearch &&
        similarRecipes.length > 0 &&
        !isLoading &&
        hasMoreSemanticResults &&
        recipes.length + similarRecipes.length < 50 &&
        !isSemanticLoading
      "
      class="text-center mt-3 mb-4"
    >
      <button
        @click="fetchMoreSemanticResults"
        class="btn btn-outline-secondary"
      >
        Показать ещё
      </button>
    </div>

    <!-- Scroll to top button -->
    <ScrollToTop />
  </div>
</template>

<script>
import RecipeView from "./RecipeView.vue";
import ScrollToTop from "./ScrollToTop.vue";
import ChatRecipe from "./ChatRecipe.vue";
import RecipeCardPopular from "./RecipeCardPopular.vue";

import { uuid } from "vue-uuid";

const cmdRecipesList = "recipes.list/";
const cmdDishTypesList = "dishtypes.list";
const cmdMyRecipesList = "recipe.my/";
const cmdRecipeSearchFast = "recipe.search.fast/";
const cmdRecipeSearchSemantic = "recipe.search.semantic/";
const cmdLikedRecipesList = "recipe.liked/";
const cmdCategoriesList = "categories.list";
const cmdAuthorRecipesList = "recipe.byAuthor/";
const cmdCookedRecipesList = "recipe.cooked/";
const cmdInterestedRecipesList = "recipe.interested/";
const cmdInitChat = "chat.init_with_answer";

export default {
  name: "RecipeList",

  components: { RecipeView, ScrollToTop, RecipeCardPopular, ChatRecipe },

  props: {
    /**
     * The mode of the recipe list.
     * Valid values: "all", "my-recipes".
     * @type {string}
     */
    mode: {
      type: String,
      default: "all",
    },
    /**
     * Whether to show the semantic search mode toggle switch.
     * @type {boolean}
     */
    showSearchModeTogle: {
      type: Boolean,
      default: true,
    },

    /**
     * Whether to show the filter inputs (dish type, category, max time).
     * @type {boolean}
     */
    showFilters: {
      type: Boolean,
      default: true,
    },

    /**
     * The name of the author whose recipes to display.
     * Used when mode is 'author'.
     */
    authorName: {
      type: String,
      default: "",
    },
    /**
     * Whether to use saved filters from localStorage on mount.
     * If false, filters will be reset to default values.
     * @type {boolean}
     */
    useSavedFilter: {
      type: Boolean,
      default: true,
    },
    /**
     * An initial list of recipes to display, bypassing the initial fetch.
     * Used to restore state when navigating back.
     * @type {Array|null}
     */
    initialRecipes: {
      type: Array,
      default: null,
    },
    /**
     * The scroll position to restore to when using initialRecipes.
     * @type {Number}
     */
    restoreScroll: {
      type: Number,
      default: 0,
    },
    /**
     * Default search mode to use if the toggle is hidden.
     * Valid values: "semantic", "sql".
     * @type {string}
     */
    searchMode: {
      type: String,
      default: "sql", // Default to SQL search if toggle is hidden
      validator: function (value) {
        return ["semantic", "sql"].indexOf(value) !== -1;
      },
    },
  },

  /**
   * Component data
   * @typedef {Object} Data
   * @property {Recipe[]} recipes - List of recipes
   * @property {DishType[]} dishTypes - List of dish types
   * @property {Category[]} categories - List of categories
   * @property {Filters} filters - Filters for the recipe list
   * @property {string[]} searchHistory - List of search history
   * @property {boolean} useSemanticSearch - Whether to use semantic search
   * @property {boolean} useSearchGenerate - Whether to use semantic search generation
   * @property {string} historyFilterText - Text to filter search history dropdown
   * @property {boolean} isLoading - Whether the component is loading
   */
  data() {
    return {
      /**
       * List of recipes
       * @type {Recipe[]}
       */
      recipes: [],
      /**
       * List of similar recipes from semantic search.
       * @type {Recipe[]}
       */
      similarRecipes: [],

      /**
       * List of dish types
       * @type {DishType[]}
       */
      dishTypes: [],

      /**
       * List of categories
       * @type {Category[]}
       */
      categories: [],

      /**
       * Filters for the recipe list
       * @type {Filters}
       */
      filters: {},

      /**
       * List of search history
       * @type {string[]}
       */
      searchHistory: [],

      /**
       * Whether to use semantic search
       * @type {boolean}
       */
      useSemanticSearch: false,

      /**
       * Whether to use semantic search generation
       * @type {boolean}
       */
      useSearchGenerate: false,

      /**
       * Text to filter search history dropdown
       * @type {string}
       */
      historyFilterText: "",

      /**
       * Whether the component is loading
       * @type {boolean}
       */
      isLoading: false,
      /**
       * Whether the semantic part of the search is loading.
       * @type {boolean}
       */
      isSemanticLoading: false,
      /**
       * Whether a semantic search pagination call is in progress.
       * @type {boolean}
       */
      isPaginatingSemantic: false,
      /**
       * Counter for consecutive empty semantic search fetches.
       * @type {number}
       */
      consecutiveEmptyFetches: 0,
      /**
       * Flag to indicate if there are more semantic search results to load.
       * @type {boolean}
       */
      hasMoreSemanticResults: true,
      /**
       * Flag to indicate if a new semantic search has been initiated.
       * @type {boolean}
       */
      isNewSemanticSearch: true,

      /**
       * Pagination token for fetching the next set of recipes in SQL mode.
       * Typically, this is the offset for the next query. "0" for the first page.
       * @type {string}
       */
      previousRecipesToken: "0",
      /**
       * Flag to prevent multiple simultaneous requests for the next page.
       * @type {boolean}
       */
      processGetNextPage: false,
      /**
       * The current display mode for the recipe list ('list' or 'grid').
       * @type {string}
       */
      displayMode: "list",
      /**
       * Tracks if the search input is focused to dynamically change the UI.
       * @type {boolean}
       */
      isSearchFocused: false,
      /**
       * Stores the text answer from the AI.
       * @type {string|null}
       */
      aiAnswer: null,
      /**
       * The question that prompted the AI answer.
       * @type {string|null}
       */
      aiQuestion: null,
      /**
       * Controls the visibility of the chat component.
       * @type {boolean}
       */
      showChat: false,
      /**
       * The ID of the chat to be displayed.
       * @type {string|null}
       */
      activeChatId: null,
      /**
       * A key to force re-rendering the chat component.
       * @type {number}
       */
      chatComponentKey: 0,
    };
  },

  created() {
    this.handleUseSavedFilter();

    // Token used for the current ongoing request
    this.currentRequestPageToken = "0";

    // Load embedding engine from localStorage
    const savedEngine = localStorage.getItem("embeddingEngine");
    if (savedEngine) this.embeddingEngine = savedEngine;

    // Initialize search mode.
    if (this.mode !== "all") {
      // For any mode other than "all" (e.g., "my-recipes", "likes", "cooked"),
      // semantic search is always disabled to prevent localStorage interference.
      this.useSemanticSearch = false;
      this.useSearchGenerate = false;
    } else if (this.showSearchModeTogle) {
      // If the toggle is visible (e.g., on the main page), respect localStorage.
      this.useSemanticSearch =
        JSON.parse(localStorage.getItem("useSemanticSearch")) || false;
      this.useSearchGenerate =
        JSON.parse(localStorage.getItem("useSearchGenerate")) || false;
    } else {
      // If the toggle is hidden by a prop, use the 'searchMode' prop to decide.
      this.useSemanticSearch = this.searchMode === "semantic";
      // this.useSearchGenerate = this.searchMode === "semantic";
      // this.embeddingEngine = "openai";
      this.useSearchGenerate = false;
      this.embeddingEngine = "word2vec";
    }

    // Load search history from localStorage
    const history = localStorage.getItem("searchHistory");
    this.searchHistory = history ? JSON.parse(history) : [];

    // Load display mode from localStorage
    const savedDisplayMode = localStorage.getItem("recipeDisplayMode");
    if (savedDisplayMode && ["list", "grid"].includes(savedDisplayMode)) {
      this.displayMode = savedDisplayMode;
    }
  },

  mounted() {
    const that = this;

    // Add reader
    this.reader = this.teoweb.addReader(async function (gw, data) {
      // Make reader async for await
      const isRecipeFetchCommand =
        gw.command.startsWith(cmdRecipesList) ||
        gw.command.startsWith(cmdRecipeSearchFast) ||
        gw.command.startsWith(cmdRecipeSearchSemantic) ||
        gw.command.startsWith(cmdMyRecipesList) ||
        gw.command.startsWith(cmdLikedRecipesList) ||
        gw.command.startsWith(cmdCookedRecipesList) ||
        gw.command.startsWith(cmdInterestedRecipesList) ||
        gw.command.startsWith(cmdAuthorRecipesList);

      // Check error
      if (isRecipeFetchCommand && gw.err) {
        if (
          typeof gw.err === "string" &&
          gw.err.includes("get user data: user data is not UserData")
        ) {
          console.error(
            "RecipeList: Authentication session lost. Reloading the application."
          );
          window.location.reload();
          return; // Stop further processing
        }

        // --- Graceful failure for semantic search ---
        // If the error is specifically from the semantic search part,
        // we keep the fast search results instead of clearing everything.
        if (gw.command.startsWith(cmdRecipeSearchSemantic)) {
          console.warn(
            "Semantic search failed, but fast search results will be kept. Error:",
            gw.err
          );
          that.isSemanticLoading = false; // Stop the semantic spinner
          that.hasMoreSemanticResults = false; // Hide the "load more" button
          // Exit without clearing recipes or emitting an error event that would hide the list
          return;
        }

        // If any command fails and the loading indicator was active,
        // disable it.
        that.isPaginatingSemantic = false;
        that.isSemanticLoading = false;
        that.isLoading = false;
        console.error("Teonet error:", gw.err, "for command:", gw.command);
        that.recipes = []; // Clear recipes on error
        that.$emit("semantic-search-has-results", false); // Ensure RecipeSuggested is shown on error
        return;
      }

      // Process recipes answer
      if (isRecipeFetchCommand) {
        // Flag to prevent isLoading from being set to false if we are auto-refetching
        // let isRefetching = false;

        try {
          // data is the raw JSON string from the server, e.g.,
          // '{"data":"Base64EncodedPackedString", "pagination":"..."}'
          const responseObject = JSON.parse(data);

          if (!responseObject || typeof responseObject.data !== "string") {
            console.error(
              "RecipeList Reader: Invalid data format from server. Expected 'data' field with Base64 string.",
              responseObject
            );
            throw new Error("Invalid data format from server.");
          }

          // responseObject.data is the Base64 encoded string that window.decompressData expects
          const unpackedJsonString = await that.unpackData(responseObject.data);
          const receivedData = JSON.parse(unpackedJsonString);

          // Handle the new universal search response format
          if (gw.command.startsWith(cmdRecipeSearchSemantic)) {
            that.handleUniversalSearchResponse(receivedData);
          } else if (gw.command.startsWith(cmdRecipeSearchFast)) {
            console.debug(
              "Reader: Fast search results received:",
              receivedData.length
            );
            that.recipes = receivedData;
            that.isLoading = false; // Main loading is done
            that.$emit("semantic-search-has-results", that.recipes.length > 0);
          } else {
            // SQL search (non-semantic)
            const paginationToken = responseObject.pagination;
            const receivedRecipes = receivedData;
            if (that.currentRequestPageToken === "0") {
              // Check if this data is for the first page
              that.recipes = receivedRecipes;
            } else {
              that.recipes.push(...receivedRecipes);
            }
            that.previousRecipesToken = paginationToken; // Store token for the *next* request
            that.processGetNextPage = false; // Allow next page fetch
            that.isLoading = false;
            that.$emit("semantic-search-has-results", false); // SQL search doesn't hide RecipeSuggested
          }
        } catch (e) {
          console.error(
            "Error processing recipes data:",
            e,
            "Raw Data from server:",
            data
          );
          that.isPaginatingSemantic = false;
          that.isSemanticLoading = false;
          that.recipes = []; // Clear recipes on parse error
          that.$emit("semantic-search-has-results", false);
        } finally {
          // Loading flags are now handled inside the try block
        }
        // });
      }

      // Process dish types and categories answer
      switch (gw.command) {
        case cmdDishTypesList:
          try {
            that.dishTypes = that.normalizeList(JSON.parse(data));
          } catch (e) {
            console.error("Error parsing dish types JSON:", e, "Data:", data);
          }
          break;
        case cmdCategoriesList:
          try {
            that.categories = that.normalizeList(JSON.parse(data));
          } catch (e) {
            console.error("Error parsing categories JSON:", e, "Data:", data);
          }
          break;
      }
    });

    // Send commands when login
    this.whenLogin().then(() => {
      // If initialRecipes are provided, use them to restore state.
      // Otherwise, perform a normal fetch.
      if (this.initialRecipes) {
        this.recipes = this.initialRecipes;
        // Restore scroll position
        this.$nextTick(() => {
          window.scrollTo(0, this.restoreScroll);
        });
      } else {
        this.fetchRecipes();
      }

      // Get dish types and categories lists
      this.fetchDishTypes();
      this.fetchCategories();
      // Add scroll listener for pagination
      window.addEventListener("scroll", this.scrollListener);
    });
  },

  unmounted() {
    // Delete reader function
    this.teoweb.delReader(this.reader);
    // Remove scroll listener
    window.removeEventListener("scroll", this.scrollListener);
  },

  computed: {
    /**
     * Filters the search history based on the historyFilterText.
     */
    filteredSearchHistory() {
      if (!this.historyFilterText) {
        return this.searchHistory;
      }
      const filter = this.historyFilterText.toLowerCase();
      return this.searchHistory.filter((term) =>
        term.toLowerCase().includes(filter)
      );
    },

    /**
     * Returns the appropriate placeholder text for the search input based on the current mode.
     * @returns {string} The placeholder text.
     */
    searchPlaceholder() {
      return this.mode === "my-recipes"
        ? "Поиск по моим рецептам..."
        : "Поиск по рецептам...";
    },

    /**
     * Returns the CSS class for the current display mode.
     * @returns {string} The CSS class.
     */
    displayModeClass() {
      return this.displayMode === "grid" ? "grid-view" : "list-view";
    },
    /**
     * Formats the AI answer to be displayed as HTML.
     * @returns {string} The formatted HTML string.
     */
    formattedAiAnswer() {
      if (!this.aiAnswer) {
        return "";
      }

      let html = this.aiAnswer.replace(/\r\n/g, "\n");

      // Process Headers
      html = html.replace(
        /^## (.*$)/gim,
        "<h2 style='margin-top: 20px;'>$1</h2>"
      );
      html = html.replace(
        /^### (.*$)/gim,
        "<h3 style='margin-top: 20px;'>$1</h3>"
      );

      // Process Bold and Italic
      html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");

      // Process Lists with -
      html = html.replace(
        /^\s*[-]\s+(.*)/gm,
        "<div style='margin-left: 20px;'>- $1</div>"
      );
      html = html.replace(/<\/div>\n<div/g, "</div><div");

      // Process Lists with *
      html = html.replace(
        /^\s*[*]\s+(.*)/gm,
        "<li style='margin-left: 20px; margin-top: 10px; margin-bottom: 10px;'>$1</li>"
      );

      // Process newlines to <br> for remaining line breaks
      html = html.replace(/\n/g, "<br />"); // Replace newlines with <br>

      html = html.replace(/(<br\s*\/?>){2,}/g, "<br>"); // Remove multiple <br>
      html = html.replace(/(<\/h[1-6]>)<br\s*\/?>/g, "$1"); // Remove <br> after header
      html = html.replace(/(<\/li>)<br\s*\/?>/g, "$1"); // Remove <br> after </li>
      html = html.replace(/(<\/div>)<br\s*\/?>/g, "$1"); // Remove <br> after </div>

      return html;
    },
  },

  methods: {
    handleUseSavedFilter() {
      if (this.useSavedFilter) {
        const savedFilters = JSON.parse(localStorage.getItem("recipeFilters"));
        this.filters = {
          dishtype: (savedFilters && savedFilters.dishtype) || "",
          category: (savedFilters && savedFilters.category) || "",
          maxTime: (savedFilters && savedFilters.maxTime) || "",
          search: (savedFilters && savedFilters.search) || "",
        };
      } else {
        this.filters = {}; // Reset filters if not using saved ones
      }
    },

    /**
     * Handles the Enter key press in the search input.
     * Sets a flag and triggers the search.
     */
    handleSearchEnter() {
      this.saveSearchTerm();
      this.$refs.searchInput.blur();
    },

    /**
     * Clear the search input and save the new state.
     */
    clearSearch() {
      this.filters.search = "";
      this.saveSearchTerm();
    },

    /**
     * Save current filters to localStorage and fetch recipes.
     */
    saveFiltersAndFetch() {
      console.log("Filters before saving:", JSON.stringify(this.filters));
      localStorage.setItem("embeddingEngine", this.embeddingEngine);
      localStorage.setItem("recipeFilters", JSON.stringify(this.filters));
      this.previousRecipesToken = "0"; // Reset pagination on filter change
      this.fetchRecipes();
    },

    /**
     * Save search term to localStorage and fetch recipes.
     */
    saveSearchTerm() {
      localStorage.setItem("recipeFilters", JSON.stringify(this.filters));
      this.updateSearchHistory(this.filters.search);
      this.previousRecipesToken = "0"; // Reset pagination on search term change
      // this.recipes = []; // Optionally clear recipes here
      this.fetchRecipes();
    },

    /**
     * Update search history in localStorage.
     * @param {string} searchTerm - The search term to add to the history.
     */
    updateSearchHistory(searchTerm) {
      if (searchTerm && !this.searchHistory.includes(searchTerm)) {
        this.searchHistory.unshift(searchTerm);
        localStorage.setItem(
          "searchHistory",
          JSON.stringify(this.searchHistory)
        );
      }
    },

    /**
     * Select a search term from the history and update the search input.
     * @param {string} term - The selected search term.
     */
    selectSearchTerm(term) {
      this.filters.search = term;
      // Move selected term to the top of history
      const index = this.searchHistory.indexOf(term);
      if (index > -1) {
        this.searchHistory.splice(index, 1);
        this.searchHistory.unshift(term);
        localStorage.setItem(
          "searchHistory",
          JSON.stringify(this.searchHistory)
        );
      }
      this.saveSearchTerm();
    },

    /**
     * Remove a search term from the history.
     * @param {string} term - The search term to remove.
     */
    removeSearchTerm(term) {
      const index = this.searchHistory.indexOf(term);
      if (index > -1) {
        this.searchHistory.splice(index, 1);
        localStorage.setItem(
          "searchHistory",
          JSON.stringify(this.searchHistory)
        );
      }
    },

    /**
     * Save search mode to localStorage.
     */
    saveSearchMode() {
      localStorage.setItem(
        "useSemanticSearch",
        JSON.stringify(this.useSemanticSearch)
      );

      localStorage.setItem(
        "useSearchGenerate",
        JSON.stringify(this.useSearchGenerate)
      );

      // If switching to SQL mode, or if semantic search is on but term is empty,
      // ensure RecipeSuggested is visible.
      if (!this.useSemanticSearch) {
        this.aiAnswer = null; // Clear AI answer when switching modes
        this.showChat = false;
        this.activeChatId = null;

        this.$emit("semantic-search-has-results", false);
      } else if (!this.filters.search || this.filters.search.trim() === "") {
        this.$emit("semantic-search-has-results", false);
        this.recipes = []; // Clear recipes if search term is empty in semantic mode
        this.isLoading = false; // Stop loading if no search term for semantic
        return; // Don't fetch if semantic and no search term
      }

      // Re-fetch recipes if the search term is not empty or if we switched to SQL
      if (
        (this.useSemanticSearch &&
          this.filters.search &&
          this.filters.search.trim() !== "") ||
        !this.useSemanticSearch
      ) {
        this.fetchRecipes();
      }
    },

    /**
     * Sets the display mode for the recipe list and saves it to localStorage.
     * @param {string} mode - The display mode to set ('list' or 'grid').
     */
    setDisplayMode(mode) {
      this.displayMode = mode;
      localStorage.setItem("recipeDisplayMode", mode);
    },

    /**
     * Fetch recipes based on current filters.
     */
    fetchRecipes(isPaginatedCall = false) {
      // When a new search is initiated (not pagination), clear the cache
      if (!isPaginatedCall && this.mode === "all") {
        this.$globalState.lastSearchState = {
          searchTerm: null,
          results: null,
          scrollPosition: 0,
        };
      }

      if (!isPaginatedCall) {
        // Reset AI answer and chat state only for a completely new search
        this.aiAnswer = null;
        this.showChat = false;
        this.similarRecipes = [];
        this.activeChatId = null;
      }

      if (this.useSemanticSearch) {
        // For semantic search, only proceed if there's a search term
        if (!this.filters.search || this.filters.search.trim() === "") {
          console.debug("Semantic search skipped: no search term.");
          this.recipes = []; // Clear previous results
          this.isLoading = false;
          this.isSemanticLoading = false;
          this.$emit("semantic-search-has-results", false);
          return;
        }

        const topN = 12;
        const searchQuery = this.filters.search.trim();

        if (!isPaginatedCall) {
          // This is a new search, restore the classic two-step fetch
          this.isLoading = true;
          this.isSemanticLoading = true;
          this.recipes = [];
          this.hasMoreSemanticResults = true; // Reset for new search
          this.consecutiveEmptyFetches = 0;

          // 1. Send fast search command first for instant results
          const fastCommand = cmdRecipeSearchFast + topN;
          this.teoweb.sendCmd(fastCommand, searchQuery);
        } else {
          // This is a pagination call, only show the semantic spinner
          this.isSemanticLoading = true;
        }
        // 2. Send semantic search command immediately after for smart results
        const semanticCommand =
          cmdRecipeSearchSemantic +
          topN +
          "/" +
          (this.useSearchGenerate ? "1" : "");

        // For pagination, we need to exclude IDs from both fast and previous semantic searches.
        const excludeIds = isPaginatedCall
          ? [
              ...this.recipes.map((r) => r.id),
              ...this.similarRecipes.map((r) => r.id),
            ]
          : this.recipes.map((r) => r.id);

        const semanticPayload = {
          query: searchQuery,
          exclude_ids: excludeIds,
          engine: this.embeddingEngine,
        };

        // Store the question for the chat feature
        this.aiQuestion = this.filters.search.trim();
        this.teoweb.sendCmd(semanticCommand, JSON.stringify(semanticPayload));
      } else {
        // For SQL search
        this.isLoading = true;
        this.$emit("semantic-search-has-results", false);

        if (!isPaginatedCall) {
          this.previousRecipesToken = "0"; // Reset for new filter/search context
          // Consider clearing this.recipes here if you want immediate visual feedback
          // this.recipes = [];
        }
        this.currentRequestPageToken = this.previousRecipesToken; // Set token for the current request

        let command;
        if (this.mode === "my-recipes") {
          command =
            cmdMyRecipesList +
            this.currentRequestPageToken +
            "/" +
            (this.filters.search || "");
        } else if (this.mode === "likes") {
          command =
            cmdLikedRecipesList +
            this.currentRequestPageToken +
            "/" +
            (this.filters.search || "");
        } else if (this.mode === "cooked") {
          command =
            cmdCookedRecipesList +
            this.currentRequestPageToken +
            "/" +
            (this.filters.search || "");
        } else if (this.mode === "interested") {
          command =
            cmdInterestedRecipesList +
            this.currentRequestPageToken +
            "/" +
            (this.filters.search || "");
        } else if (this.mode === "author") {
          command =
            cmdAuthorRecipesList +
            encodeURIComponent(this.authorName) +
            "/" +
            this.currentRequestPageToken +
            "/" +
            (this.filters.search || "");
        } else {
          command =
            cmdRecipesList +
            this.currentRequestPageToken +
            "/" +
            (this.filters.dishtype || "") +
            "/" +
            (this.filters.category || "") +
            "/" +
            (this.filters.maxTime || "") +
            "/" +
            (this.filters.search || ""); // Pass search term for server-side filtering
        }
        console.debug("Sending list command:", command);
        this.teoweb.sendCmd(command); // Send command to Teonet
      }
    },

    /**
     * Fetch the list of dish types.
     */
    fetchDishTypes() {
      this.teoweb.sendCmd(cmdDishTypesList);
    },

    /**
     * Fetch the list of categories.
     */
    fetchCategories() {
      this.teoweb.sendCmd(cmdCategoriesList);
    },

    /**
     * Normalize a list of categories or dish types.
     * @param {Array} list - The list of categories or dish types.
     * @returns {Array} The normalized list.
     */
    normalizeList(list) {
      if (!list) return [];
      return list
        .map(({ name, ...rest }) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          ...rest,
        }))
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .filter(
          (c, i, self) => self.findIndex((c2) => c2.name === c.name) === i
        );
    },

    /**
     * Show full recipe.
     * @param {Object} recipe - The recipe to show.
     */
    showFullRecipe(recipe) {
      if (this.mode === "all") {
        this.$globalState.lastSearchState = {
          searchTerm: this.filters.search,
          results: this.recipes,
          scrollPosition: window.scrollY,
        };
      }

      localStorage.setItem(
        "lastViewedRecipe",
        JSON.stringify({
          from: this.mode, // 'all', 'my-recipes', etc.
          id: recipe.id,
        })
      );
      window.location.hash = `/recipes/${recipe.id}`;
    },

    /**
     * scrollListener - Checks whether the user has scrolled to the bottom of the list.
     * If so, it calls fetchNextRecipesPage to get the next page of recipes.
     * This function is called every time the user scrolls.
     */
    scrollListener() {
      if (this.useSemanticSearch) return; // No pagination in semantic search mode

      const advance = 300; // Pixels before bottom to trigger load
      const scrollTop = window.scrollY;
      const scrollHeight = document.body.scrollHeight;
      const clientHeight = window.innerHeight;

      if (scrollTop + clientHeight + advance >= scrollHeight) {
        this.fetchNextRecipesPage();
      }
    },

    /**
     * Fetches the next page of recipes if pagination is active and no request is in progress.
     */
    fetchNextRecipesPage() {
      if (
        !this.previousRecipesToken ||
        this.previousRecipesToken === "" ||
        this.processGetNextPage ||
        this.useSemanticSearch
      ) {
        return; // No more pages, or request in progress, or in semantic mode
      }
      this.processGetNextPage = true;
      this.fetchRecipes(true); // true indicates it's a paginated call for the next page
    },
    /**
     * Fetches the next page of semantic search results.
     */
    fetchMoreSemanticResults() {
      this.isPaginatingSemantic = true;
      // isPaginatedCall = true tells fetchRecipes not to reset the list
      this.fetchRecipes(true);
    },

    /**
     * Handles the status-changed event from a child RecipeCardPopular component.
     * @param {object} payload - The event payload.
     * @param {number} payload.id - The ID of the recipe that was changed.
     * @param {boolean} payload.show - The new publication status.
     */
    handleStatusChange({ id, show }) {
      const recipe = this.recipes.find((r) => r.id === id);
      if (recipe) {
        recipe.show = show;
      }
    },

    /**
     * Handles the new universal search response from the server.
     * @param {object} response - The parsed JSON response.
     * @param {Array} [response.recipes] - The list of found recipes.
     * @param {string} [response.answer] - The text answer from the AI.
     */
    handleUniversalSearchResponse(response) {
      this.isLoading = false;
      this.isSemanticLoading = false;

      const isPaginating = this.isPaginatingSemantic; // Store flag before resetting
      this.isPaginatingSemantic = false; // Reset pagination flag

      if (response.recipes) {
        const existingIds = new Set([
          ...this.recipes.map((r) => r.id),
          ...this.similarRecipes.map((r) => r.id),
        ]);
        const newRecipes = response.recipes.filter(
          (r) => !existingIds.has(r.id)
        );

        if (isPaginating) {
          this.similarRecipes.push(...newRecipes);
          if (newRecipes.length < 10) {
            // If we got less than requested, assume no more
            this.hasMoreSemanticResults = false;
          }
        } else {
          this.similarRecipes = newRecipes;
        }
      }

      if (
        this.recipes.length > 0 ||
        this.similarRecipes.length > 0 ||
        response.answer
      ) {
        this.$emit("semantic-search-has-results", true);
      } else {
        this.$emit("semantic-search-has-results", false);
      }

      if (response.answer) {
        this.aiAnswer = response.answer;
        // If there's an AI answer, we consider that a "result"
        this.$emit("semantic-search-has-results", true);
      } else {
        this.aiAnswer = null;
      }
    },

    /**
     * Handles the click on the "Continue dialog" button.
     */
    async continueDialog() {
      // 1. Generate a unique chat ID on the client
      const newChatId = `search-${uuid.v1()}`;
      this.activeChatId = newChatId;

      // 2. Send the initial question and answer to the server to be saved
      const payload = {
        chat_id: newChatId,
        question: this.aiQuestion,
        answer: this.formattedAiAnswer,
      };
      try {
        await this.sendCmd(cmdInitChat, JSON.stringify(payload));

        // 3. Hide the answer and show the chat component
        this.aiAnswer = null;
        this.showChat = true;
        this.chatComponentKey += 1; // Force re-render of the chat component
      } catch (error) {
        console.error("Error sending chat init message:", error);
      }
    },
  },

  watch: {
    useSavedFilter() {
      this.handleUseSavedFilter();
    },
    isLoading(newVal) {
      this.$emit("update:isLoading", newVal);
    },
    isSemanticLoading(newVal) {
      this.$emit("update:isSemanticLoading", newVal);
    },
  },
};
</script>

<style scoped>
.recipe ul,
.recipe ol {
  padding-left: 1.4rem;
}

.recipe-list-container.list-view .recipe-item:not(:first-child) {
  border-top: 1px solid var(--bs-border-color-translucent);
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

.recipe-list-container.grid-view {
  display: grid;
  /* Default to 4 columns for desktop view */
  grid-template-columns: repeat(4, 1fr);
  gap: 0.98em;
}

/* Media query for smaller screens (e.g., mobile phones) */

@media (max-width: 800.98px) {
  .recipe-list-container.grid-view {
    /* On mobile, switch to 2 columns */
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }
}

@media (max-width: 600.98px) {
  .recipe-list-container.grid-view {
    /* On mobile, switch to 2 columns */
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
}

@media (max-width: 374.98px) {
  .recipe-list-container.grid-view {
    /* On mobile, switch to 2 columns */
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.75rem;
  }
}

.filter {
  max-width: 529px;
}

.filter select,
.filter input {
  margin-right: 0.4rem;
}

.filter .dishtype {
  max-width: 130px;
}

.filter .category {
  max-width: 130px;
}

.filter .maxTime {
  max-width: 175px;
}

.search-history-dropdown {
  max-height: 500px;
  overflow-y: auto;
}

.search-history-term {
  cursor: pointer;
  width: 100%;
}

.input-group {
  position: relative;
  height: 50px;
}

.input-group button {
  border-color: var(--bs-border-color-translucent);
}

.filter-input {
  border-right: none;
}

.close {
  margin-left: 0 !important;
  border-left: none;
  padding: 10px;
  width: 24px;
}

.close i {
  margin-left: -6px;
}

.history {
  border-color: var(--bs-border-color-translucent);
}
</style>
